// Fenêtre « Compléter un compartiment » : une commande du même produit complète un compartiment entamé.
export const COMPARTMENT_SHARING_CHALLENGE_CODE = "compartment_sharing_confirmation_required";
export const COMPARTMENT_SHARING_STALE_CODE = "compartment_sharing_confirmation_stale";

const formatTons = (value) => `${new Intl.NumberFormat("fr-CA", { maximumFractionDigits: 3 }).format(Number(value) || 0)} t`;
const joinLabels = (labels) => (labels.length > 1 ? `${labels.slice(0, -1).join(", ")} et ${labels.at(-1)}` : labels[0] || "");
const unique = (values) => [...new Set(values.filter(Boolean))];

export function resolveCompartmentSharingChallenge({ payload, response } = {}) {
  if (response?.status !== 409 || payload?.code !== COMPARTMENT_SHARING_CHALLENGE_CODE) return null;
  const details = payload.error_details;
  const compartments = Array.isArray(details?.shared_compartments) ? details.shared_compartments : [];
  if (!details?.confirmation_id || !details.runtime_revision || !compartments.length
    || compartments.some((item) => !Number.isFinite(Number(item?.position)) || !Array.isArray(item.added) || !item.added.length)) return null;
  return details;
}

// Relance construite à partir du corps qui a reçu le défi : les confirmations déjà données sont gardées.
export function buildCompartmentSharingRetryBody(requestBody, details, decision) {
  if (!requestBody || typeof requestBody !== "object" || !details) return null;
  return {
    ...requestBody,
    compartment_sharing_decision: decision,
    compartment_sharing_confirmation_id: details.confirmation_id,
    expected_runtime_revision: details.runtime_revision,
  };
}

// Chargé PM et Preload : sans partage, l'action est refusée (pas de « Garder deux chargements »).
const REFUSED_WITHOUT_SHARING = {
  end_of_day_load: "Sans partage, le Chargé PM est refusé.",
  preloaded_previous_day: "Sans partage, « Chargé la veille » est refusé.",
};

export function buildCompartmentSharingConfirmationContent(details, targetTruckLabel = "") {
  const compartments = details.shared_compartments;
  const labels = compartments.map((item) => `C${item.position}`);
  const quantities = new Map((details.moved_orders || []).map((order) => [order.order_id, order.quantity_tons]));
  const added = unique(compartments.flatMap((item) => item.added.map((order) => order.order_id)));
  const existing = unique(compartments.flatMap((item) => (item.existing || []).map((order) => order.order_id)));
  const addedLabel = joinLabels(added.map((orderId) => (quantities.has(orderId) ? `${orderId} (${formatTons(quantities.get(orderId))})` : orderId)));
  const truck = targetTruckLabel || details.target_truck_label || details.target_truck_id;
  const product = [details.product?.grain_code, details.product?.grain_type].filter(Boolean).join(" ");
  const portions = (orders) => joinLabels(orders.map((order) => `${formatTons(order.quantity_tons)} de ${order.order_id}${order.loaded_previous_day ? ", chargée la veille" : ""}`));
  const refusedSentence = details.without_sharing === "refused"
    ? REFUSED_WITHOUT_SHARING[details.action] || "Sans partage, l'action est refusée."
    : "";
  // Un compartiment déjà entamé à bord est complété; sinon, les commandes déplacées se le partagent.
  const completed = compartments.filter((item) => item.existing?.length);
  const splitBetweenMoved = compartments.filter((item) => !item.existing?.length);
  const unloads = compartments.flatMap((item) => (item.existing?.length ? item.existing : item.added).map((order) => `chez ${order.order_id}, le chauffeur déchargera seulement ${formatTons(order.quantity_tons)} de C${item.position}`));
  const unloadSentence = unloads.length ? `${unloads.join(" ; ").replace(/^chez/, "Chez")}.` : "";
  const plural = added.length > 1;
  return {
    title: `Compléter ${joinLabels(labels)} avec ${joinLabels(added)} ?`,
    description: [
      `${addedLabel} ${plural ? "n'entrent pas" : "n'entre pas"}${plural && !existing.length ? " ensemble" : ""} dans les compartiments vides du camion ${truck}${existing.length ? ` avec ${joinLabels(existing)} à bord` : ""}.`,
      completed.length ? `${plural ? "Elles peuvent" : "Elle peut"} compléter ${joinLabels(completed.map((item) => `C${item.position}, qui contient déjà ${portions(item.existing)}`))}${product ? ` (même produit ${product})` : ""}.` : "",
      splitBetweenMoved.length ? `Elles peuvent se partager ${splitBetweenMoved.map((item) => `C${item.position} : ${portions(item.added)}`).join(" ; ")}${product && !completed.length ? `, même produit ${product}` : ""}.` : "",
      unloadSentence,
      refusedSentence || (existing.length
        ? `Sans partage, ${joinLabels(added)} ${plural ? "attendent" : "attend"} que ${joinLabels(existing)} ${existing.length > 1 ? "soient livrées" : "soit livrée"} (retour à la meunerie).`
        : "Sans partage, le camion fait deux chargements (retour à la meunerie)."),
    ].filter(Boolean).join(" "),
    confirmLabel: `Compléter ${joinLabels(labels)}`,
    secondaryLabel: refusedSentence ? "" : "Garder deux chargements",
    cancelLabel: "Annuler",
    summaryItems: compartments.map((item) => ({
      label: `C${item.position} · ${formatTons(item.effective_capacity_tons ?? item.capacity_tons)}`,
      value: [...(item.existing || []), ...item.added].map((order) => `${order.order_id} (${formatTons(order.quantity_tons)})`).join(" + "),
    })),
  };
}

export function createCompartmentSharingChallengeHandler({
  dismissConfirmation, retryMutation, setConfirmationDialog, targetTruckLabel = "",
} = {}) {
  return ({ payload, requestBody, response } = {}) => {
    if (typeof retryMutation !== "function" || typeof setConfirmationDialog !== "function") return false;
    const details = resolveCompartmentSharingChallenge({ payload, response });
    const shareBody = buildCompartmentSharingRetryBody(requestBody, details, "share");
    const keepBody = buildCompartmentSharingRetryBody(requestBody, details, "keep_separate");
    if (!details || !shareBody || !keepBody || requestBody.compartment_sharing_decision) return false;
    const content = buildCompartmentSharingConfirmationContent(details, targetTruckLabel);
    let settled = false;
    const close = () => {
      settled = true;
      if (typeof dismissConfirmation === "function") dismissConfirmation();
      else setConfirmationDialog(null);
    };
    const retry = (requestBodyForDecision) => {
      if (settled) return;
      close();
      retryMutation({ requestBody: requestBodyForDecision, requestExpectedRuntimeRevision: details.runtime_revision });
    };
    setConfirmationDialog({
      isOpen: true,
      eyebrow: "Compartiments",
      title: content.title,
      description: content.description,
      confirmLabel: content.confirmLabel,
      cancelLabel: content.cancelLabel,
      secondaryAction: content.secondaryLabel ? { label: content.secondaryLabel, onClick: () => retry(keepBody) } : null,
      tone: "accent",
      cornerRadius: "grain",
      panelSize: "compact",
      contentDensity: "compact",
      summaryDensity: "compact",
      summaryLayout: "stack",
      summaryItems: content.summaryItems,
      onCancel: close,
      onClose: close,
      onDismiss: close,
      onConfirm: () => retry(shareBody),
    });
    return true;
  };
}
